/**
 * 저장된 뷰 — **필터 조합에 이름을 붙여 다시 부른다.**
 *
 * 필터 축은 이미 여덟이다(작업자·작성자·유형·우선순위·라벨·검색어·
 * 디바이스·환경). 자주 쓰는 조합을 매번 다시 조립하는 것이 유일하게
 * 남은 마찰이라, **새로 만든 것은 「저장과 이름」뿐이다.**
 *
 * ## 왜 서버가 아니라 브라우저인가
 *
 * 표를 만들면 마이그레이션·API·권한이 따라오고, 그 값은 **셋이 쓰는
 * 도구에서 「내가 자주 보는 것」 하나를 위해 치르기에 크다.** 지금은
 * 사람마다 자기 브라우저에 남는다 — 남과 나눌 일이 생기면 그때 표로
 * 옮기고, 이 모듈만 바꾸면 화면은 그대로다.
 *
 * ⚠ **`localStorage` 는 없을 수도 있고 던질 수도 있다**(사생활 보호 창,
 * 사이트 자료 차단). 읽기·쓰기를 모두 감싸고, 실패하면 **빈 목록으로
 * 조용히 돌아간다** — 저장된 뷰가 없다고 보드가 안 뜨면 안 된다.
 *
 * ⚠ **프로젝트마다 따로 담는다.** 라벨 id·작업자 id 가 프로젝트를 넘으면
 * 뜻이 없거나 다른 것을 가리킨다.
 */

import type { TicketFilter } from '../api';

export type SavedView = {
  name: string;
  filter: TicketFilter;
};

/** 이름 길이 상한. 칩으로 서는 값이라 길면 도구줄이 무너진다. */
export const NAME_MAX = 20;

/** 한 프로젝트에 담는 최대 개수. 넘으면 칩 줄이 목록이 된다. */
export const MAX_VIEWS = 8;

function key(projectId: number): string {
  return `plusme-office:saved-views:${projectId}`;
}

/**
 * 걸린 필터가 있나. **없으면 저장할 것이 없다** — 도구줄이 「안 정한 것은
 * 안 그린다」는 원칙을 따르므로, 이 판정이 저장 단추를 그릴지도 정한다.
 */
export function hasFilter(filter: TicketFilter): boolean {
  return Object.values(filter).some((v) => v !== null && v !== undefined && v !== '');
}

/**
 * 두 필터가 같은가. 칩이 「지금 이 뷰를 보고 있다」를 표시하는 데 쓴다.
 *
 * 키 순서에 기대지 않는다 — `JSON.stringify` 로 견주면 `{a,b}` 와
 * `{b,a}` 가 다르다고 나온다.
 */
export function sameFilter(a: TicketFilter, b: TicketFilter): boolean {
  const clean = (f: TicketFilter): [string, unknown][] =>
    Object.entries(f)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .sort(([x], [y]) => (x < y ? -1 : 1));
  const left = clean(a);
  const right = clean(b);
  if (left.length !== right.length) return false;
  return left.every(([k, v], i) => right[i][0] === k && right[i][1] === v);
}

/** 담긴 것. 형식이 깨졌으면 **버리고 빈 목록을 준다.** */
export function loadViews(projectId: number): SavedView[] {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key(projectId));
  } catch {
    // 저장소가 아예 없거나(jsdom·일부 내장 브라우저) 차단됐다.
    return [];
  }
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item): item is SavedView =>
          typeof item === 'object' &&
          item !== null &&
          typeof (item as SavedView).name === 'string' &&
          typeof (item as SavedView).filter === 'object' &&
          (item as SavedView).filter !== null,
      )
      .slice(0, MAX_VIEWS);
  } catch {
    return [];
  }
}

/** 담는다. 실패해도 던지지 않는다 — 저장이 안 됐다고 화면이 멈추면 안 된다. */
export function storeViews(projectId: number, views: SavedView[]): void {
  try {
    window.localStorage.setItem(key(projectId), JSON.stringify(views.slice(0, MAX_VIEWS)));
  } catch {
    /* 사생활 보호 창·자료 차단. 이번 세션에서만 안 남는다. */
  }
}

/**
 * 이름을 다듬는다. 빈 이름이면 `null` — 부르는 쪽이 저장을 그만둔다.
 *
 * 같은 이름이 있으면 **덮어쓴다.** 같은 이름 둘이 나란히 서면 어느 것이
 * 무엇인지 칩만 보고는 알 수 없다.
 */
export function addView(
  views: SavedView[],
  name: string,
  filter: TicketFilter,
): SavedView[] | null {
  const clean = name.trim().slice(0, NAME_MAX);
  if (!clean) return null;
  const rest = views.filter((v) => v.name !== clean);
  return [...rest, { name: clean, filter }].slice(-MAX_VIEWS);
}

export function removeView(views: SavedView[], name: string): SavedView[] {
  return views.filter((v) => v.name !== name);
}
